/**
 * src/lib/router.js
 *
 * Tiny hash router. Every screen is a routed view (#/dashboard, #/attendance,
 * …) instead of an overlay/modal-stack — this is what makes the browser back
 * button actually work (CK's "no back button" / "broken overlay" complaints
 * from the Flutter-web desktop shell). Hash assignment pushes a history entry
 * natively, so no manual pushState bookkeeping is needed.
 *
 * GO-LIVE HARDENING (2026-07-23): every registered mount fn in shell.js's
 * SCREEN_MOUNTERS is `async (el) => (await import('../screens/x.js')).mountX(el)`
 * — a dynamic import(). If that chunk fails to load (the routine "user has an
 * old tab open across a redeploy, clicks a nav item whose hashed chunk no
 * longer exists on the CDN" case for any code-split SPA) or a mount fn throws
 * synchronously, `render()` used to have zero error handling: `container.
 * innerHTML = ''` had already run, so the failure left #content-region
 * silently blank with nothing but a console-only unhandled-rejection — a real
 * dead-end (customer clicks a nav item, gets a blank white screen, no error,
 * no retry). There was also no window-level error/unhandledrejection handler
 * anywhere in the app to catch it. render() now wraps the mount call so a
 * failure paints a small recoverable error state instead — see
 * reportRenderFailure below.
 */

const routes = new Map();
const listeners = [];
let container = null;

/** @param {string} path e.g. '/dashboard' @param {(el: HTMLElement, query?: Object<string,string>) => void} renderFn */
export function registerRoute(path, renderFn) {
  routes.set(path, renderFn);
}

/**
 * splitPath — separates an optional `?key=value` query suffix from a hash
 * path (e.g. '/employees?action=new' → { path: '/employees', query:
 * { action: 'new' } }). Registered routes are keyed by the BARE path, so a
 * query-carrying deep link (quick actions, /customer-statement?…) resolves
 * to the same route its bare path does — bare paths behave exactly as
 * before (query = {}).
 */
export function splitPath(fullPath) {
  const qIdx = (fullPath || '').indexOf('?');
  if (qIdx === -1) return { path: fullPath, query: {} };
  return {
    path: fullPath.slice(0, qIdx),
    query: Object.fromEntries(new URLSearchParams(fullPath.slice(qIdx + 1))),
  };
}

/** Subscribe to route changes (for updating active-nav state, page title, …).
 *
 * LEAK FIX (2026-08-19): returns an unsubscribe function. `listeners` used to
 * be append-only — shell.js/employee_shell.js push a new closure (capturing
 * the whole shell `root` DOM tree) on EVERY mount, and a sign-out → sign-in
 * cycle mounts again, so each cycle permanently retained a dead shell tree
 * and ran its active-nav update + recents write against detached DOM on all
 * future route changes. Callers keep their unsub handle (mirroring the
 * existing _langListenerUnsub pattern) and resetRouteListeners() below is the
 * auth-transition backstop. */
export function onRouteChange(cb) {
  listeners.push(cb);
  return () => {
    const i = listeners.indexOf(cb);
    if (i !== -1) listeners.splice(i, 1);
  };
}

/** Drops ALL route-change listeners. Called by main.js on every auth-state
 * transition (next to resetBootstrapCache) so a persona switch (admin shell →
 * sign-out → employee shell) can never strand the OTHER shell's listener —
 * each shell's own unsub handle only covers re-mounts of the SAME shell. */
export function resetRouteListeners() {
  listeners.length = 0;
}

export function getCurrentPath() {
  const hash = window.location.hash || '';
  const path = hash.startsWith('#') ? hash.slice(1) : hash;
  return path || '/dashboard';
}

/** Navigate — sets location.hash, which the browser records in history. */
export function navigate(path) {
  window.location.hash = `#${path}`;
}

export function startRouter(containerEl) {
  container = containerEl;
  window.addEventListener('hashchange', render);
  if (!window.location.hash) {
    window.location.hash = '#/dashboard';
  } else {
    render();
  }
}

function render() {
  const { path, query } = splitPath(getCurrentPath());
  const renderFn = routes.get(path) || routes.get('__notfound__');
  if (container) {
    container.innerHTML = '';
    if (renderFn) {
      try {
        const result = renderFn(container, query);
        // Every real screen mount fn is `async (el) => (await import(...)).mountX(el)`
        // — catch a rejected dynamic import() / an async mount's thrown error
        // the same way a synchronous throw is caught below.
        if (result && typeof result.catch === 'function') {
          result.catch((err) => reportRenderFailure(path, err));
        }
      } catch (err) {
        reportRenderFailure(path, err);
      }
    }
  }
  // Listeners receive the BARE path (query stripped) so active-nav /
  // recents matching against registered screen paths keeps working for
  // query-carrying deep links.
  for (const cb of listeners) cb(path);
}

/**
 * reportRenderFailure — a screen's mount fn threw, or its dynamic import()
 * rejected. Logs the real error for diagnostics and paints a small
 * recoverable state into #content-region instead of leaving it blank.
 *
 * Guards against a stale/slow failure clobbering a NEWER screen: if the user
 * has already navigated elsewhere by the time this settles (async import
 * rejections can arrive well after the click), `getCurrentPath()` will no
 * longer match `path` and this is a no-op — the screen that's actually on
 * screen now is left alone.
 */
function reportRenderFailure(path, err) {
  console.error('[router] screen mount failed for', path, err);
  if (!container || splitPath(getCurrentPath()).path !== path) return;

  // STALE-BUNDLE SELF-HEAL: a failed dynamic import() after a redeploy is the
  // common "old tab open across a ship, hashed chunk no longer on the CDN"
  // case, and it is fixed deterministically by a full reload (fresh index.html
  // -> fresh chunk hashes). Auto-reload ONCE per short window so the user never
  // sees a blank OR an error card for that case — the app self-heals. A
  // sessionStorage guard bounds it: if a reload already happened <20s ago (i.e.
  // the fresh bundle ALSO failed — a genuine persistent problem, not staleness),
  // fall through to the manual error card instead of looping.
  if (isChunkLoadError(err)) {
    try {
      const now = Date.now();
      const last = Number(sessionStorage.getItem('__staffly_chunk_reload_at') || 0);
      if (!last || now - last > 20000) {
        sessionStorage.setItem('__staffly_chunk_reload_at', String(now));
        window.location.reload();
        return;
      }
    } catch (_) { /* sessionStorage blocked -> fall through to the error card */ }
  }

  container.innerHTML = renderErrorHtml();
  container.querySelector('[data-router-reload]')?.addEventListener('click', () => {
    window.location.reload();
  });
}

/** True when `err` is a failed dynamic import() (a code-split chunk that no
 * longer exists on the CDN after a redeploy, or a transient network failure
 * fetching it) — as opposed to a genuine bug thrown inside a screen's mount
 * fn. Browsers phrase this differently (Chrome/Firefox/Safari), so match on
 * the union of their messages. */
function isChunkLoadError(err) {
  const m = String((err && (err.message || err)) || '').toLowerCase();
  return (
    m.includes('dynamically imported module') || // Chrome: "Failed to fetch dynamically imported module"
    m.includes('importing a module script failed') || // Safari
    m.includes('error loading dynamically imported module') || // Firefox
    m.includes('chunkloaderror') ||
    (m.includes('failed to fetch') && m.includes('module'))
  );
}

/** No i18n dependency here on purpose — router.js is a low-level primitive
 * shared by both the admin and employee shells and this path only fires on a
 * genuine failure (chunk-load error / mount throw), not normal navigation —
 * a plain bilingual string beats importing the full i18n stack into the
 * router's hot path. Reuses the app's existing `.empty-state`/`.btn` classes
 * (see screens/placeholder.js) so it looks native, not bolted on. */
function renderErrorHtml() {
  const ar = (typeof document !== 'undefined' && document.documentElement.getAttribute('dir') === 'rtl');
  const msg = ar ? 'تعذّر تحميل هذه الشاشة.' : "This screen couldn't load.";
  const hint = ar
    ? 'قد يكون هذا بسبب تحديث حديث للتطبيق. حاول إعادة تحميل الصفحة.'
    : 'This can happen after a recent app update. Try reloading the page.';
  const retry = ar ? 'إعادة تحميل الصفحة' : 'Reload page';
  return `
    <div class="empty-state">
      <strong>${msg}</strong>
      <div>${hint}</div>
      <div style="margin-top:10px"><button type="button" class="btn btn-ghost btn-sm" data-router-reload>${retry}</button></div>
    </div>`;
}
